import { useCallback, useEffect, useRef, useState } from 'react'
import {
  LocalAudioTrack,
  LocalParticipant,
  Participant,
  ParticipantKind,
  Room,
  RoomEvent,
  Track,
  TranscriptionSegment,
  createLocalAudioTrack,
  RemoteParticipant,
  RemoteTrackPublication,
  RemoteTrack,
} from 'livekit-client'
import { labLiveKitAPI } from '@renderer/api/chat'

/** Topic the worker listens on for screenshot byte streams. */
const SCREENSHOT_TOPIC = 'lab.screenshot'

// Module-level analyser for the agent's audio output — used by SpeakingVisualizer
// without prop threading. createMediaStreamSource (unlike createMediaElementSource)
// can be called multiple times and doesn't affect the <audio> element playback.
let _agentAnalyserCtx: AudioContext | null = null
let _agentAnalyserNode: AnalyserNode | null = null

export function getAgentAudioAnalyser(): AnalyserNode | null {
  return _agentAnalyserNode
}

/**
 * Take and immediately release the mic before connecting.
 *
 * Without a stored mic grant for this origin, Chrome obfuscates the host ICE
 * candidate as `<uuid>.local` (mDNS); LiveKit cannot resolve it and drops it,
 * leaving the server nothing to probe back. Touching getUserMedia first makes
 * candidate gathering emit real host candidates. Push-to-talk still owns the
 * real mic in startTalking().
 */
async function ensureMicPermission(): Promise<void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((t) => t.stop())
  } catch {
    // Denied or unavailable — connect anyway; the session is still usable
    // for text chat, and startTalking() surfaces its own error.
  }
}

export function resumeAgentAudioContext(): void {
  _agentAnalyserCtx?.resume()
}

/**
 * Decode a `data:image/jpeg;base64,…` URL into raw bytes.
 *
 * The capture path produces a data URL (cheaper to call .toDataURL than
 * to convert the canvas to a Blob — the former is sync, the latter
 * needs a callback or a separate offscreen canvas). For byte-stream
 * publishing we strip the `data:…;base64,` prefix and decode.
 */
function _dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(',')
  if (comma < 0) return null
  try {
    const b64 = dataUrl.slice(comma + 1)
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

/**
 * Publish a screenshot to the worker as a LiveKit byte stream.
 *
 * Byte streams chunk transparently under the hood, removing the 65,535-
 * byte hard limit that publishData()'s reliable data channel imposes.
 * That cap was forcing the JPEG quality ladder to fall back to 800-wide
 * / q=0.4 on busy frames — at which point taskbar icon labels were
 * unreadable to the vision model (5px text after downscale + JPEG) and
 * the agent fell back to "do you see the icon?" hedging.
 *
 * With byte streams we can ship 1600-wide / q=0.85 unconditionally
 * (~250-400KB), which keeps icon labels and small dialog text legible.
 *
 * The worker registers a byte-stream handler on ``lab.screenshot``,
 * concatenates the chunks, builds a data URL, and stores it in
 * room_state.screenshot — same downstream path as before.
 *
 * Returns once the stream is closed so callers can await ordering with
 * subsequent text/voice turns.
 */
async function _sendScreenshot(
  room: Room,
  dataUrl: string,
  opts: { reason?: 'turn' | 'settled'; hash?: string | null } = {}
): Promise<void> {
  const bytes = _dataUrlToBytes(dataUrl)
  if (!bytes) {
    console.warn('[useLiveKitAgent] could not decode screenshot data URL')
    return
  }
  const hash = opts.hash !== undefined ? opts.hash : hashLabScreen()
  const attributes: Record<string, string> = { reason: opts.reason ?? 'turn' }
  if (hash) attributes.hash = hash

  const writer = await room.localParticipant.streamBytes({
    topic: SCREENSHOT_TOPIC,
    mimeType: 'image/jpeg',
    totalSize: bytes.byteLength,
    attributes,
  })
  try {
    await writer.write(bytes)
  } finally {
    await writer.close()
  }
}

export interface AgentTextMessage {
  id: string
  role: 'user' | 'agent'
  text: string
  ts: number
  /** False while the segment is still being streamed; true once complete. */
  final: boolean
}

/**
 * A copy-pasteable artifact dropped into the chat by the agent's
 * `post_command` function tool. Rendered as a code card with a copy
 * button instead of a normal message bubble.
 */
export interface ChatArtifactMessage {
  id: string
  role: 'agent'
  /** Discriminator for the renderer. */
  kind: 'command' | 'snippet' | 'path' | 'value'
  /** Syntax-highlighting hint for "command" / "snippet" kinds. */
  language: string
  /** The exact text the student should copy. */
  content: string
  /** Optional short caption shown above the card. */
  note: string | null
  ts: number
}

interface UseLiveKitAgentOptions {
  /** The exercise to connect to. Hook is a no-op when undefined. */
  exerciseId: string | number | undefined
  /**
   * Optional: pass an existing conversation_id to resume a session.
   * The backend will find-or-create a LabSession and return the room name.
   */
  conversationId?: string | null
  /** Auto-connect on mount. Default true. */
  autoConnect?: boolean
}

export interface ThinkingState {
  text: string
  /** False while thinking is still streaming; true once the block is complete. */
  done: boolean
}

interface UseLiveKitAgentReturn {
  connected: boolean
  connecting: boolean
  error: string | null
  talking: boolean
  agentStatus: 'idle' | 'listening' | 'processing' | 'speaking'
  agentMessages: AgentTextMessage[]
  /**
   * Copy-pasteable artifacts (commands, paths, snippets, values) that
   * the agent dropped via the `post_command` function tool. Order is
   * insertion order; merge into the message stream by `ts`.
   */
  chatArtifacts: ChatArtifactMessage[]
  conversationId: string | null
  sessionReady: boolean
  completedTaskIds: number[]
  unverifiedTaskIds: number[]
  currentTaskId: number | null
  exerciseComplete: boolean
  jumpRejection: { taskId: number; reason: string } | null
  /** Live thinking content from the current agent turn, null between turns. */
  thinking: ThinkingState | null
  connect: (teachingMode?: 'guide' | 'assist') => Promise<void>
  disconnect: () => void
  startTalking: () => Promise<void>
  stopTalking: () => Promise<void>
  /**
   * Publish a typed user turn. Returns once both the screenshot data
   * packet and the chat text have flushed onto the reliable channel,
   * so the agent's on_user_turn_completed hook always sees the latest
   * screen before the LLM call fires.
   */
  sendUserText: (text: string) => Promise<void>
  /**
   * Publish a screenshot data-URL. Awaitable so callers can sequence it
   * before subsequent user turns.
   */
  sendScreenshot: (dataUrl: string) => Promise<void>
  sendJumpToTask: (taskId: number) => Promise<boolean>
  /** Current assistance mode, mirrored from the backend ack. */
  assistMode: 'guide' | 'assist'
  sendAssistMode: (mode: 'guide' | 'assist') => Promise<void>
  sendHelpRequest: () => Promise<void>
  helpPending: boolean
  helpRejection: string | null
}

const AGENT_JOIN_TIMEOUT_MS = 6000
const MAX_AGENT_JOIN_RETRIES = 5

const SETTLE_MS = 3000
const SETTLE_MIN_GAP_MS = 6000
const SETTLE_POLL_MS = 1000

function isAgentParticipant(p: RemoteParticipant): boolean {
  if (p.kind === ParticipantKind.AGENT) return true
  if (p.identity.startsWith('agent-')) return true
  return !p.identity.startsWith('learner-') && p.identity.length > 0
}

export function useLiveKitAgent({
  exerciseId,
  conversationId: requestedConversationId,
  autoConnect = true
}: UseLiveKitAgentOptions): UseLiveKitAgentReturn {
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [talking, setTalking] = useState(false)
  const [agentMessages, setAgentMessages] = useState<AgentTextMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(
    requestedConversationId ?? null
  )
  // Task-progress mirror, fed by data-channel events emitted by the
  // backend voice agent. The sidebar reads these (via useLabSession) to
  // render strikethroughs and the active-task highlight.
  const [sessionReady, setSessionReady] = useState(false)
  const [completedTaskIds, setCompletedTaskIds] = useState<number[]>([])
  const [unverifiedTaskIds, setUnverifiedTaskIds] = useState<number[]>([])
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null)
  const [exerciseComplete, setExerciseComplete] = useState(false)
  const [jumpRejection, setJumpRejection] =
    useState<{ taskId: number; reason: string } | null>(null)
  const [thinking, setThinking] = useState<ThinkingState | null>(null)
  const [chatArtifacts, setChatArtifacts] = useState<ChatArtifactMessage[]>([])
  const [assistMode, setAssistMode] = useState<'guide' | 'assist'>('guide')
  const [helpPending, setHelpPending] = useState(false)
  const [helpRejection, setHelpRejection] = useState<string | null>(null)
  const [agentStatus, setAgentStatus] = useState<'idle' | 'listening' | 'processing' | 'speaking'>('idle')

  const teachingModeRef = useRef<'guide' | 'assist'>('guide')
  const roomRef = useRef<Room | null>(null)
  const talkingRef = useRef(false)
  const micTrackRef = useRef<LocalAudioTrack | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  // Track the connecting flag in a ref so the callback closure stays stable
  const connectingRef = useRef(false)
  // Cancellation flag: set by disconnect() to abort any in-flight connect()
  // so the async continuation doesn't race with cleanup.
  const cancelledRef = useRef(false)
  // Watchdog retry state
  const retryCountRef = useRef(0)
  const cancelTokenRef = useRef(0)
  const watchdogIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const helpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ─── Lifecycle ────────────────────────────────────────────────────────
  const clearWatchdog = useCallback(() => {
    if (watchdogIntervalRef.current !== null) {
      clearInterval(watchdogIntervalRef.current)
      watchdogIntervalRef.current = null
    }
  }, [])

  const connect = useCallback(async (teachingMode?: 'guide' | 'assist') => {
    if (!exerciseId) return
    if (roomRef.current || connectingRef.current) return

    if (teachingMode) {
      teachingModeRef.current = teachingMode
      setAssistMode(teachingMode)
    }

    cancelledRef.current = false
    connectingRef.current = true
    setConnecting(true)
    setError(null)

    try {
      // Let the backend find-or-create the LabSession and return the room name.
      const { token, url, conversation_id } = await labLiveKitAPI.getToken(
        exerciseId,
        requestedConversationId ?? null,
        teachingModeRef.current
      )

      // If disconnect() was called while we were awaiting the token, bail out
      // cleanly — do not create a Room at all.
      if (cancelledRef.current) return

      setConversationId(conversation_id)

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Prevent LiveKit from automatically disconnecting when the page
        // becomes hidden (electron / tab-switch scenarios). We manage the
        // lifecycle ourselves via disconnect().
        disconnectOnPageLeave: false,
      })
      roomRef.current = room

      // Auto-play remote agent audio via a hidden <audio> element.
      room.on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) => {
          if (track.kind !== Track.Kind.Audio) return
          if (!audioElementRef.current) {
            audioElementRef.current = document.createElement('audio')
            audioElementRef.current.autoplay = true
            document.body.appendChild(audioElementRef.current)
          }
          track.attach(audioElementRef.current)

          // Build analyser from the track's MediaStream so SpeakingVisualizer
          // can animate bars in real-time from the agent's actual audio.
          // createMediaStreamSource doesn't affect <audio> element playback.
          try {
            const stream = (track as any).mediaStream as MediaStream | undefined
            if (stream) {
              _agentAnalyserCtx?.close()
              _agentAnalyserCtx = new AudioContext()
              const source = _agentAnalyserCtx.createMediaStreamSource(stream)
              _agentAnalyserNode = _agentAnalyserCtx.createAnalyser()
              _agentAnalyserNode.fftSize = 128
              _agentAnalyserNode.smoothingTimeConstant = 0.7
              source.connect(_agentAnalyserNode)
            }
          } catch (e) {
            console.warn('[useLiveKitAgent] agent analyser setup failed:', e)
          }
        }
      )

      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach()
        _agentAnalyserCtx?.close()
        _agentAnalyserCtx = null
        _agentAnalyserNode = null
      })

      // Drive speaking/idle from LiveKit's own audio activity so the UI
      // works even if the backend never sends agent_status events.
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        const agentSpeaking = speakers.some((p) => p !== room.localParticipant)
        setAgentStatus((prev) => {
          if (agentSpeaking) return 'speaking'
          if (prev === 'speaking') return 'idle'
          return prev
        })
      })

      // Native LiveKit transcription events.
      // Agent segments are streamed (non-final → final) for a typewriter effect.
      // User STT segments are only shown once final to avoid partial text flicker.
      room.on(
        RoomEvent.TranscriptionReceived,
        (segments: TranscriptionSegment[], participant?: Participant) => {
          const isAgent = participant && participant !== room.localParticipant
          // Clear thinking as soon as the agent's answer starts transcribing —
          // the answer phase has begun, so the thinking accordion should vanish.
          if (isAgent && segments.some((s) => s.text?.trim())) {
            setThinking(null)
            setAgentStatus('speaking')
          }
          // Agent final segment means the turn is complete → back to idle.
          if (isAgent && segments.some((s) => s.final)) {
            setAgentStatus('idle')
          }
          // User's final STT segment means the agent has received the turn and
          // is now running its LLM — show processing until audio comes back.
          if (!isAgent && segments.some((s) => s.final && s.text?.trim())) {
            setAgentStatus('processing')
          }
          for (const seg of segments) {
            if (!seg.text?.trim()) continue
            if (!isAgent && !seg.final) continue  // user voice: final only
            const msg: AgentTextMessage = {
              id: seg.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              role: isAgent ? 'agent' : 'user',
              text: seg.text,
              ts: Date.now(),
              final: seg.final,
            }
            setAgentMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === msg.id)
              if (idx >= 0) {
                const next = [...prev]
                next[idx] = msg
                return next
              }
              return [...prev, msg]
            })
          }
        }
      )

      // Data channel — task-progress events from the backend agent.
      // We listen for:
      //   {type: "session_ready"}                  → unlock jump-to-task
      //   {type: "task_active",    task_id: N}     → highlight in sidebar
      //   {type: "task_completed", task_id: N}     → strike in sidebar
      //   {type: "jump_to_task_rejected", reason}  → debug only for now
      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        let msg: any
        try {
          msg = JSON.parse(new TextDecoder().decode(payload))
        } catch {
          return
        }
        const t = msg?.type
        if (t === 'session_ready') {
          setSessionReady(true)
          return
        }
        if (t === 'task_active' && typeof msg.task_id === 'number') {
          setCurrentTaskId(msg.task_id)
          // Mirror the backend's gate: if we receive a task_active before a
          // session_ready (e.g. on first paint after the welcome utterance),
          // the agent is by definition ready, so flip the flag here too.
          setSessionReady(true)
          // Send a fresh screenshot so the backend can inject it into context
          // at the task boundary — the agent needs to see the current screen
          // when it introduces the new task, not the stale screenshot from the
          // previous user turn.
          const shot = captureLabScreenshot()
          if (shot) {
            _sendScreenshot(room, shot).catch((e) =>
              console.warn('[useLiveKitAgent] task-transition screenshot failed:', e)
            )
          }
          return
        }
        if (t === 'task_completed' && typeof msg.task_id === 'number') {
          const id = msg.task_id
          const weak = msg.source === 'inferred' || msg.source === 'self_report'
          setCompletedTaskIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
          setUnverifiedTaskIds((prev) => {
            if (weak) return prev.includes(id) ? prev : [...prev, id]
            return prev.includes(id) ? prev.filter((x) => x !== id) : prev
          })
          return
        }
        if (t === 'task_uncompleted' && typeof msg.task_id === 'number') {
          const id = msg.task_id
          setCompletedTaskIds((prev) => prev.filter((x) => x !== id))
          setUnverifiedTaskIds((prev) => prev.filter((x) => x !== id))
          return
        }
        if (t === 'exercise_complete') {
          setExerciseComplete(true)
          return
        }
        if (t === 'jump_to_task_rejected') {
          console.warn('[useLiveKitAgent] jump_to_task rejected:', msg)
          setJumpRejection({
            taskId: typeof msg.task_id === 'number' ? msg.task_id : -1,
            reason: typeof msg.reason === 'string' ? msg.reason : 'unknown'
          })
          return
        }
        if (t === 'help_request_accepted') {
          setHelpPending(false)
          return
        }
        if (t === 'help_request_rejected') {
          console.warn('[useLiveKitAgent] help_request rejected:', msg)
          setHelpPending(false)
          setHelpRejection(
            typeof msg.reason === 'string' ? msg.reason : 'unknown'
          )
          setAgentStatus('idle')
          return
        }
        if (t === 'assist_mode' && (msg.mode === 'guide' || msg.mode === 'assist')) {
          setAssistMode(msg.mode)
          return
        }
        if (t === 'agent_thinking' && typeof msg.text === 'string') {
          setThinking({ text: msg.text, done: !!msg.done })
          return
        }
        if (t === 'agent_status' && typeof msg.status === 'string') {
          const s = msg.status
          if (s === 'listening' || s === 'processing' || s === 'speaking') {
            setAgentStatus(s)
          } else {
            setAgentStatus('idle')
          }
          return
        }
        if (t === 'chat_artifact' && typeof msg.content === 'string' && msg.content) {
          const kindRaw = typeof msg.kind === 'string' ? msg.kind : 'command'
          const kind: ChatArtifactMessage['kind'] =
            kindRaw === 'snippet' || kindRaw === 'path' || kindRaw === 'value'
              ? kindRaw
              : 'command'
          const artifact: ChatArtifactMessage = {
            id: `artifact-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role: 'agent',
            kind,
            language: typeof msg.language === 'string' && msg.language ? msg.language : 'text',
            content: msg.content,
            note: typeof msg.note === 'string' && msg.note ? msg.note : null,
            ts: Date.now(),
          }
          setChatArtifacts((prev) => [...prev, artifact])
          return
        }
      })

      room.on(RoomEvent.Disconnected, () => {
        setConnected(false)
        connectingRef.current = false
      })

      // If disconnect() fired while we were setting up listeners, abort before
      // the expensive WebRTC PC negotiation starts.
      if (cancelledRef.current) {
        room.removeAllListeners()
        roomRef.current = null
        return
      }

      // Must precede connect(): candidates are gathered during the handshake.
      await ensureMicPermission()

      await room.connect(url, token)

      // One final cancellation check — in case disconnect() arrived during the
      // WebRTC handshake. The room is connected but we immediately tear it down.
      if (cancelledRef.current) {
        room.removeAllListeners()
        room.disconnect()
        roomRef.current = null
        return
      }

      setConnected(true)

      // Watchdog: poll until the agent participant appears or retry limit hit.
      const watchdogStart = Date.now()
      const myCancelToken = cancelTokenRef.current
      clearWatchdog()
      watchdogIntervalRef.current = setInterval(() => {
        if (cancelTokenRef.current !== myCancelToken) {
          clearWatchdog()
          return
        }
        const currentRoom = roomRef.current
        if (!currentRoom) {
          clearWatchdog()
          return
        }
        const participants = Array.from(currentRoom.remoteParticipants.values())
        const hasAgent = participants.some(isAgentParticipant)
        if (hasAgent) {
          console.warn('[useLiveKitAgent] agent detected, watchdog standing down:', participants.find(isAgentParticipant)?.identity)
          retryCountRef.current = 0
          clearWatchdog()
          return
        }
        const elapsed = Date.now() - watchdogStart
        if (elapsed < AGENT_JOIN_TIMEOUT_MS) return

        if (retryCountRef.current >= MAX_AGENT_JOIN_RETRIES) {
          console.warn(`[useLiveKitAgent] agent never joined after ${MAX_AGENT_JOIN_RETRIES} retries, giving up`)
          clearWatchdog()
          setError('Agent failed to join. Please try reconnecting.')
          const r = roomRef.current
          if (r) { r.removeAllListeners(); r.disconnect(); roomRef.current = null }
          audioElementRef.current?.remove()
          audioElementRef.current = null
          connectingRef.current = false
          setConnected(false)
          setTalking(false)
          setSessionReady(false)
          setCompletedTaskIds([])
          setUnverifiedTaskIds([])
          setCurrentTaskId(null)
          setExerciseComplete(false)
          setJumpRejection(null)
          setThinking(null)
          setChatArtifacts([])
          setAgentStatus('idle')
          return
        }

        retryCountRef.current += 1
        const attemptNum = retryCountRef.current
        const backoffMs = attemptNum === 1 ? 250 : 1500
        clearWatchdog()
        console.warn(`[useLiveKitAgent] agent didn't join in ${AGENT_JOIN_TIMEOUT_MS}ms — retry ${attemptNum}/${MAX_AGENT_JOIN_RETRIES}`)

        void (async () => {
          // Tear down current room without marking as user-cancelled
          const r = roomRef.current
          if (r) { r.removeAllListeners(); r.disconnect(); roomRef.current = null }
          audioElementRef.current?.remove()
          audioElementRef.current = null
          connectingRef.current = false
          setConnected(false)
          setTalking(false)
          setSessionReady(false)
          setCompletedTaskIds([])
          setUnverifiedTaskIds([])
          setCurrentTaskId(null)
          setExerciseComplete(false)
          setJumpRejection(null)
          setThinking(null)
          setChatArtifacts([])
          setAgentStatus('idle')
          setAgentMessages([])

          await new Promise<void>(resolve => setTimeout(resolve, backoffMs))
          if (cancelTokenRef.current !== myCancelToken) {
            console.warn(`[useLiveKitAgent] retry ${attemptNum} cancelled by user`)
            return
          }
          console.warn(`[useLiveKitAgent] firing retry ${attemptNum}`)
          try {
            await connect()
          } catch (e) {
            console.error('[useLiveKitAgent] retry connect failed:', e)
            setError('Agent failed to join. Please try reconnecting.')
          }
        })()
      }, 500)
    } catch (e: any) {
      if (!cancelledRef.current) {
        console.error('[useLiveKitAgent] connect() failed:', e)
        setError(e?.message ?? String(e))
      }
      if (roomRef.current) {
        roomRef.current.removeAllListeners()
        roomRef.current.disconnect()
        roomRef.current = null
      }
    } finally {
      connectingRef.current = false
      setConnecting(false)
    }
  }, [exerciseId, requestedConversationId, clearWatchdog])

  const disconnect = useCallback(() => {
    // Abort any in-flight watchdog retries and clear the timer.
    cancelTokenRef.current += 1
    retryCountRef.current = 0
    clearWatchdog()
    // Signal any in-flight connect() to abort after its next await.
    cancelledRef.current = true

    micTrackRef.current?.stop()
    micTrackRef.current = null
    if (roomRef.current) {
      roomRef.current.removeAllListeners()
      roomRef.current.disconnect()
      roomRef.current = null
    }
    audioElementRef.current?.remove()
    audioElementRef.current = null
    connectingRef.current = false
    setConnected(false)
    setTalking(false)
    // Clear task-progress mirror so a fresh connect doesn't render stale
    // strikethroughs from the previous session.
    setSessionReady(false)
    setCompletedTaskIds([])
    setUnverifiedTaskIds([])
    setCurrentTaskId(null)
    setExerciseComplete(false)
    setJumpRejection(null)
    setThinking(null)
    setChatArtifacts([])
    setAgentStatus('idle')
    setAgentMessages([])
    if (helpTimeoutRef.current !== null) {
      clearTimeout(helpTimeoutRef.current)
      helpTimeoutRef.current = null
    }
    setHelpPending(false)
    setHelpRejection(null)
  }, [clearWatchdog])

  useEffect(() => { talkingRef.current = talking }, [talking])

  useEffect(() => {
    if (!connected || !sessionReady) return

    console.info('[settle] armed')
    let lastHash: string | null = null
    let stillSince = Date.now()
    let fired = true          // nothing has changed yet; nothing to report
    let lastSentAt = 0

    let armedBy: 'hash' | 'input' = 'hash'
    const onStudentInput = (): void => {
      if (fired) armedBy = 'input'
      fired = false
      stillSince = Date.now()
    }
    const inputEvents = ['keydown', 'pointerdown', 'wheel'] as const
    for (const ev of inputEvents) {
      document.addEventListener(ev, onStudentInput, {
        capture: true,
        passive: true,
      })
    }

    const tick = setInterval(() => {
      const hash = hashLabScreen()
      if (!hash) return                       // no canvas yet

      if (hashDistance(hash, lastHash) > HASH_TOLERANCE) {
        if (lastHash !== null) {
          if (fired) armedBy = 'hash'
          fired = false
        }
        lastHash = hash
        stillSince = Date.now()
        return
      }

      if (fired) return
      const now = Date.now()
      if (now - stillSince < SETTLE_MS) return
      if (now - lastSentAt < SETTLE_MIN_GAP_MS) return

      const room = roomRef.current
      if (!room) return
      if (talkingRef.current) return

      const pane = document.querySelector(
        '[data-lab-rdp-container]'
      ) as HTMLElement | null
      if (!pane || pane.offsetParent === null) return

      const shot = captureLabScreenshot()
      if (!shot) return

      fired = true
      lastSentAt = now
      console.info('[settle] sending', { hash, armedBy })
      _sendScreenshot(room, shot, { reason: 'settled', hash }).catch((e) =>
        console.warn('[useLiveKitAgent] settled screenshot failed:', e)
      )
    }, SETTLE_POLL_MS)

    return () => {
      console.info('[settle] disarmed')
      clearInterval(tick)
      for (const ev of inputEvents) {
        document.removeEventListener(ev, onStudentInput, { capture: true })
      }
    }
  }, [connected, sessionReady])

  useEffect(() => {
    if (!autoConnect || !exerciseId) return
    connect()
    return () => { disconnect() }
    // Re-run when exerciseId changes (navigating between labs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId, autoConnect])

  // ─── Mic control ──────────────────────────────────────────────────────
  const startTalking = useCallback(async () => {
    const room = roomRef.current
    if (!room || talking) return
    try {
      // Capture and send screenshot at mic-press time so the agent sees
      // the current screen when the user turn completes. Await the publish
      // BEFORE we publish the mic track — STT will start producing a user
      // turn as soon as audio arrives, and we want the screenshot in
      // room_state.screenshot before on_user_turn_completed fires.
      const shot = captureLabScreenshot()
      if (shot) {
        try {
          await _sendScreenshot(room, shot)
        } catch (e) {
          console.warn('[useLiveKitAgent] screenshot publish failed (voice):', e)
        }
      }

      const track = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      })
      micTrackRef.current = track
      await (room.localParticipant as LocalParticipant).publishTrack(track, {
        name: 'mic',
        source: Track.Source.Microphone
      })
      setTalking(true)
    } catch (e: any) {
      setError(`mic: ${e?.message ?? String(e)}`)
    }
  }, [talking])

  const stopTalking = useCallback(async () => {
    const room = roomRef.current
    const track = micTrackRef.current
    if (!room || !track) {
      setTalking(false)
      return
    }
    try {
      await (room.localParticipant as LocalParticipant).unpublishTrack(track, true)
    } catch {
      // ignore
    }
    track.stop()
    micTrackRef.current = null
    setTalking(false)
    // User released mic → agent is now receiving and processing the turn.
    setAgentStatus('processing')
  }, [])

  const sendUserText = useCallback(async (text: string): Promise<void> => {
    const room = roomRef.current
    const trimmed = text.trim()
    if (!room || !trimmed) return
    // Capture screenshot at send time so both text and voice turns go
    // through the same screenshot path. Await the publish before sending
    // the chat text so the screenshot lands in room_state.screenshot
    // before the agent's lk.chat handler fires generate_reply().
    const shot = captureLabScreenshot()
    if (shot) {
      try {
        await _sendScreenshot(room, shot)
      } catch (e) {
        console.warn('[useLiveKitAgent] screenshot publish failed (text):', e)
      }
    }
    await room.localParticipant.sendText(trimmed, { topic: 'lk.chat' })
    setAgentStatus('processing')
  }, [])

  const sendScreenshot = useCallback(
    async (dataUrl: string): Promise<void> => {
      const room = roomRef.current
      if (!room || !dataUrl) return
      try {
        await _sendScreenshot(room, dataUrl)
      } catch (e) {
        console.warn('[useLiveKitAgent] sendScreenshot failed:', e)
      }
    },
    []
  )

  const sendJumpToTask = useCallback(
    async (taskId: number): Promise<boolean> => {
      const room = roomRef.current
      if (!room || !sessionReady || !Number.isFinite(taskId)) return false

      // Await the screenshot before publishing the jump packet so the
      // backend receives the screenshot first and has no race to wait out.
      // Mirrors sendUserText exactly — no backend wait needed.
      const shot = captureLabScreenshot()
      if (shot) {
        try {
          await _sendScreenshot(room, shot)
        } catch (e) {
          console.warn('[useLiveKitAgent] screenshot publish failed (jump):', e)
        }
      }

      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'jump_to_task', task_id: taskId })
      )
      room.localParticipant.publishData(payload, { reliable: true })
      return true
    },
    [sessionReady]
  )

  const sendHelpRequest = useCallback(async (): Promise<void> => {
    const room = roomRef.current
    if (!room || !sessionReady) return

    setHelpRejection(null)
    setHelpPending(true)
    if (helpTimeoutRef.current !== null) clearTimeout(helpTimeoutRef.current)
    helpTimeoutRef.current = setTimeout(() => setHelpPending(false), 15000)

    const shot = captureLabScreenshot()
    if (shot) {
      try {
        await _sendScreenshot(room, shot)
      } catch (e) {
        console.warn('[useLiveKitAgent] screenshot publish failed (help):', e)
      }
    }

    const payload = new TextEncoder().encode(
      JSON.stringify({ type: 'help_request' })
    )
    room.localParticipant.publishData(payload, { reliable: true })
    setAgentStatus('processing')
  }, [sessionReady])

  const sendAssistMode = useCallback(async (mode: 'guide' | 'assist') => {
    setAssistMode(mode)
    teachingModeRef.current = mode
    const room = roomRef.current
    if (!room) return

    if (mode === 'guide') {
      const shot = captureLabScreenshot()
      if (shot) {
        try {
          await _sendScreenshot(room, shot)
        } catch (e) {
          console.warn('[useLiveKitAgent] screenshot publish failed (mode):', e)
        }
      }
    }

    const payload = new TextEncoder().encode(
      JSON.stringify({ type: 'set_assist_mode', mode })
    )
    room.localParticipant.publishData(payload, { reliable: true })
  }, [])

  return {
    connected,
    connecting,
    error,
    talking,
    agentStatus,
    agentMessages,
    chatArtifacts,
    conversationId,
    sessionReady,
    completedTaskIds,
    unverifiedTaskIds,
    currentTaskId,
    exerciseComplete,
    jumpRejection,
    thinking,
    connect,
    disconnect,
    startTalking,
    stopTalking,
    sendUserText,
    sendScreenshot,
    sendJumpToTask,
    assistMode,
    sendAssistMode,
    sendHelpRequest,
    helpPending,
    helpRejection,
  }
}

/**
 * Capture the current frame of the embedded Guacamole canvas, downscale to
 * fit a max width, and JPEG-encode. Returns a data URL or null.
 *
 * Screenshots are sent over LiveKit byte streams (see _sendScreenshot),
 * which chunk transparently — there is no 64KB cap to fight any more.
 * That lets us hold resolution and quality high so the vision model can
 * read small text (icon labels, terminal output, status-bar text).
 *
 * Default settings:
 *   - 1600-wide downscale (text clarity scales nearly linearly with
 *     resolution; icon labels stay legible at this width).
 *   - q=0.85 JPEG (visually lossless for text; ~200-400KB for a busy
 *     1920×1080 desktop scaled to 1600).
 *
 * The (width, quality) ladder still exists as a SAFETY fallback only —
 * if a frame somehow exceeds ``softCapBytes`` (default 2 MB) we step
 * down rather than ship a single huge stream. In practice this almost
 * never triggers; q=0.85 at 1600 wide stays well under 1 MB.
 */
/** Reused across ticks — allocating a canvas at 1Hz is pointless churn. */
let _hashCanvas: HTMLCanvasElement | null = null
let _hashCtx: CanvasRenderingContext2D | null = null

const HASH_W = 32
const HASH_H = 18
export const HASH_TOLERANCE = 4

export function hashLabScreen(): string | null {
  const src = findLabCanvas()
  if (!src || !src.width || !src.height) return null

  if (!_hashCanvas) {
    _hashCanvas = document.createElement('canvas')
    _hashCanvas.width = HASH_W
    _hashCanvas.height = HASH_H
    _hashCtx = _hashCanvas.getContext('2d', { willReadFrequently: true })
  }
  const ctx = _hashCtx
  if (!ctx) return null

  try {
    ctx.imageSmoothingEnabled = true
    ctx.drawImage(src, 0, 0, HASH_W, HASH_H)
    const { data } = ctx.getImageData(0, 0, HASH_W, HASH_H)

    const lums = new Array<number>(HASH_W * HASH_H)
    let total = 0
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
      const lum = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000
      lums[p] = lum
      total += lum
    }
    const mean = total / lums.length

    let hex = ''
    for (let i = 0; i < lums.length; i += 4) {
      let nibble = 0
      for (let b = 0; b < 4; b += 1) {
        if (lums[i + b] > mean) nibble |= 1 << b
      }
      hex += nibble.toString(16)
    }
    return `${src.width}x${src.height}:${hex}`
  } catch (e) {
    console.warn('[useLiveKitAgent] screen hash failed:', e)
    return null
  }
}

export function hashDistance(a: string | null, b: string | null): number {
  if (!a || !b) return Infinity
  const [resA, hexA] = a.split(':')
  const [resB, hexB] = b.split(':')
  if (resA !== resB || !hexA || !hexB || hexA.length !== hexB.length) {
    return Infinity
  }
  let bits = 0
  for (let i = 0; i < hexA.length; i += 1) {
    let x = parseInt(hexA[i], 16) ^ parseInt(hexB[i], 16)
    while (x) {
      bits += x & 1
      x >>= 1
    }
  }
  return bits
}

function findLabCanvas(): HTMLCanvasElement | null {
  const roots: ParentNode[] = []
  const rdp = document.querySelector('[data-lab-rdp-container]')
  if (rdp) roots.push(rdp)
  roots.push(document)
  for (const root of roots) {
    let best: HTMLCanvasElement | null = null
    let bestArea = 0
    for (const c of root.querySelectorAll('canvas')) {
      const cv = c as HTMLCanvasElement
      const area = cv.width * cv.height
      if (area > bestArea) {
        bestArea = area
        best = cv
      }
    }
    if (best) return best
  }
  return null
}

export function captureLabScreenshot(
  options: { maxWidth?: number; softCapBytes?: number } = {}
): string | null {
  const { maxWidth = 1600, softCapBytes = 2_000_000 } = options

  let src: HTMLCanvasElement | null = null

  const rdpContainer = document.querySelector('[data-lab-rdp-container]')
  if (rdpContainer) {
    const rdpCanvases = rdpContainer.querySelectorAll('canvas')
    let bestArea = 0
    for (const c of rdpCanvases) {
      const cv = c as HTMLCanvasElement
      const area = cv.width * cv.height
      if (area > bestArea) {
        bestArea = area
        src = cv
      }
    }
  }

  if (!src) {
    const canvases = document.querySelectorAll('canvas')
    if (canvases.length === 0) return null
    let bestArea = 0
    for (const c of canvases) {
      const cv = c as HTMLCanvasElement
      const area = cv.width * cv.height
      if (area > bestArea) {
        bestArea = area
        src = cv
      }
    }
  }
  if (!src) return null
  const sw = src.width
  const sh = src.height
  if (!sw || !sh) return null

  // Width tiers — first one is the target, the rest only kick in if the
  // result blows past ``softCapBytes``. Never upscale, and dedupe.
  //
  // The target is CLAMPED to the source rather than filtered against it.
  // Filtering was dropping every tier wider than the canvas, which on a
  // narrow display left the emergency fallback as the ONLY option: a
  // 1152x576 Guacamole canvas filtered [1600, 1280, 1024] down to [1024],
  // so every frame shipped at 1024x512 — a downscale of a source that was
  // already small, in service of a 2 MB cap these frames miss by 50x
  // (measured 2026-10-12: 35-50 KB each). The `length === 0` guard below
  // only ever fired for a canvas narrower than 1024, so nothing caught it.
  //
  // That resolution is not free. The step judge is asked whether an
  // expected result is "unambiguously visible", and some of those results
  // enumerate nine licence names in terminal text; on 2026-10-12 a student
  // ran `system license show` and the judge reported the output missing on
  // every frame until they gave up and reset the exercise.
  const widthTiers = [Math.min(maxWidth, sw), 1280, 1024]
    .filter((w) => w <= sw)
    .filter((w, i, arr) => arr.indexOf(w) === i)
  if (widthTiers.length === 0) widthTiers.push(sw)

  const qualityTiers = [0.85, 0.7]

  try {
    for (const targetWidth of widthTiers) {
      const scale = Math.min(1, targetWidth / sw)
      const dw = Math.round(sw * scale)
      const dh = Math.round(sh * scale)

      const tmp = document.createElement('canvas')
      tmp.width = dw
      tmp.height = dh
      const ctx = tmp.getContext('2d')
      if (!ctx) continue
      ctx.imageSmoothingEnabled = true
      ;(ctx as any).imageSmoothingQuality = 'high'
      ctx.drawImage(src, 0, 0, dw, dh)

      for (const quality of qualityTiers) {
        const dataUrl = tmp.toDataURL('image/jpeg', quality)
        // Each base64 char ≈ 1 byte. Soft cap is generous so the
        // happy path (1600 / 0.85) almost always returns immediately.
        if (dataUrl.length <= softCapBytes) return dataUrl
      }
    }
    // If even the smallest tier blew past the cap something is very
    // weird (e.g. the canvas is bigger than 4K). Fall back to the last
    // encoded URL anyway — better stale-quality than no screenshot.
    return null
  } catch {
    return null
  }
}
